import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface FeatureTracking {
  enabled: boolean;
  firstUsedAt?: string;
  lastUsedAt?: string;
  lastDisabledAt?: string;
  usageCount?: number;
  [key: string]: any;
}

interface FeatureAdoptionTimelineProps {
  features: Record<string, FeatureTracking>;
}

interface TimelineFeature {
  name: string;
  adoptedAt: string;
  lastUsedAt?: string;
  isEnabled: boolean;
  usageCount?: number;
}

/**
 * Feature Adoption Timeline Component
 * Shows chronological adoption of features
 */
export const FeatureAdoptionTimeline: React.FC<FeatureAdoptionTimelineProps> = ({
  features,
}) => {
  // Extract features with adoption timestamps
  const timelineFeatures: TimelineFeature[] = [];

  if (features) {
    Object.entries(features).forEach(([featureName, feature]) => {
      // Only include features that have a firstUsedAt timestamp (adoption date)
      if (feature.firstUsedAt) {
        timelineFeatures.push({
          name: featureName,
          adoptedAt: feature.firstUsedAt,
          lastUsedAt: feature.lastUsedAt,
          isEnabled: feature.enabled,
          usageCount: feature.usageCount,
        });
      }
    });
  }

  // Sort by adoption date (newest first)
  timelineFeatures.sort((a, b) => new Date(b.adoptedAt).getTime() - new Date(a.adoptedAt).getTime());

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatFeatureName = (name: string): string => {
    // Convert camelCase to Title Case with spaces
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  if (timelineFeatures.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No feature adoption timeline available
      </Typography>
    );
  }

  return (
    <Box>
      {timelineFeatures.map((feature, index) => (
        <Box
          key={feature.name}
          sx={{
            display: 'flex',
            gap: 2,
            mb: index < timelineFeatures.length - 1 ? 3 : 0,
            position: 'relative',
          }}
        >
          {/* Timeline line */}
          {index < timelineFeatures.length - 1 && (
            <Box
              sx={{
                position: 'absolute',
                left: 12,
                top: 32,
                bottom: -24,
                width: 2,
                backgroundColor: feature.isEnabled ? 'success.light' : 'grey.300',
              }}
            />
          )}

          {/* Timeline dot */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: feature.isEnabled ? 'success.main' : 'grey.400',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.5,
              zIndex: 1,
            }}
          >
            {feature.isEnabled && <CheckCircle sx={{ fontSize: 16, color: 'white' }} />}
          </Box>

          {/* Content */}
          <Box flex={1}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: feature.isEnabled ? 'success.50' : 'grey.50',
                borderColor: feature.isEnabled ? 'success.main' : 'grey.300',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {formatFeatureName(feature.name)}
                </Typography>
                <Chip
                  label={feature.isEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={feature.isEnabled ? 'success' : 'default'}
                />
              </Box>

              <Box>
                <Box display="flex" gap={2} alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Adopted:</strong> {formatDate(feature.adoptedAt)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({getRelativeTime(feature.adoptedAt)})
                  </Typography>
                </Box>
                {feature.lastUsedAt && feature.lastUsedAt !== feature.adoptedAt && (
                  <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      <strong>Last used:</strong> {formatDate(feature.lastUsedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({getRelativeTime(feature.lastUsedAt)})
                    </Typography>
                  </Box>
                )}
                {feature.usageCount && feature.usageCount > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Used {feature.usageCount} times
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
